import React, { useState, useRef } from 'react';
import { documentProcessingService } from '../services/documentProcessingService';
import { useAuth, useTrip } from '@trip-planner-monorepo/shared';
import { useParams } from 'react-router-dom';
import { FaUpload, FaSpinner, FaCloudUploadAlt, FaFileAlt } from 'react-icons/fa';
import './DocumentUpload.css';
import { ALLOWED_FILE_TYPES, MAX_FILE_SIZE_BYTES } from '../constants/fileValidation';

const DocumentUpload = ({ onProcessed, onFileSelected }) => {
    const [file, setFile] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState('');
    const [dragActive, setDragActive] = useState(false);
    const [dragCounter, setDragCounter] = useState(0);
    
    const { currentUser } = useAuth();
    const { effectiveUserId } = useTrip();
    const { tripId } = useParams();
    const fileInputRef = useRef(null);
    const dragAreaRef = useRef(null);
    
    /**
     * Validates a file for upload
     * @param {File} selectedFile - The file to validate
     * @returns {Object} Validation result with isValid boolean and error message
     */
    const validateFile = (selectedFile) => {
        if (!selectedFile) {
            return { isValid: false, error: 'No file selected.' };
        }

        // File type validation
        if (!ALLOWED_FILE_TYPES.includes(selectedFile.type)) {
            return { 
                isValid: false, 
                error: 'Unsupported file type. Please upload a PDF or image file (PNG, JPG, JPEG).' 
            };
        }

        // File size validation
        if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
            const maxSizeMB = MAX_FILE_SIZE_BYTES / (1024 * 1024);
            return { 
                isValid: false, 
                error: `File is too large. Maximum allowed size is ${maxSizeMB}MB.` 
            };
        }

        return { isValid: true, error: '' };
    };

    /**
     * Handles file selection from input or drag and drop
     * @param {File} selectedFile - The selected file
     */
    const handleFileSelection = (selectedFile) => {
        const validation = validateFile(selectedFile);
        
        if (!validation.isValid) {
            setError(validation.error);
            setFile(null);
            return;
        }

        setFile(selectedFile);
        setError('');
        
        // Notify parent component if onFileSelected callback is provided
        if (onFileSelected) {
            onFileSelected(selectedFile);
        }
    };
    
    /**
     * Handles file input change (click-to-upload)
     */
    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        handleFileSelection(selectedFile);
    };

    /**
     * Handles drag enter event
     */
    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragCounter(prev => prev + 1);
        setDragActive(true);
    };

    /**
     * Handles drag leave event
     */
    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragCounter(prev => prev - 1);
        if (dragCounter <= 1) {
            setDragActive(false);
        }
    };

    /**
     * Handles drag over event
     */
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    /**
     * Handles drop event
     */
    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        setDragActive(false);
        setDragCounter(0);
        
        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const selectedFile = files[0];
            handleFileSelection(selectedFile);
        }
    };

    /**
     * Handles click on drag area to trigger file input
     */
    const handleDragAreaClick = () => {
        fileInputRef.current?.click();
    };
    
    /**
     * Handles form submission
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!file) {
            setError('Please select a file to upload');
            return;
        }
        
        setProcessing(true);
        setError('');
        
        try {
            // Use the new AI-integrated processing service
            const result = await documentProcessingService.processDocument(
                file,
                effectiveUserId,
                tripId
            );
            
            console.log('Document processing result:', result);
            
            // Show processing method to user for transparency
            if (result.processingMethod === 'ai') {
                console.log('Document processed with AI:', result.aiResults);
            } else if (result.processingMethod === 'fallback') {
                console.warn('AI processing failed, used fallback:', result.error);
                setError(`Document uploaded successfully, but AI processing failed: ${result.error}`);
            } else {
                console.log('Document processed without AI (basic mode)');
            }
            
            if (result && onProcessed) {
                onProcessed(result);
            }
            
            setFile(null);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } catch (error) {
            console.error('Upload error:', error);
            setError(`Failed to process document: ${error.message}. Please try again.`);
        } finally {
            setProcessing(false);
        }
    };

    /**
     * Formats file size for display
     * @param {number} bytes - File size in bytes
     * @returns {string} Formatted file size
     */
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };
    
    return (
        <div className="document-upload">
            {error && <div className="error-message">{error}</div>}
            
            <form onSubmit={handleSubmit}>
                <div className="drag-drop-container">
                    <input
                        ref={fileInputRef}
                        type="file"
                        id="document-file"
                        onChange={handleFileChange}
                        accept=".pdf,.png,.jpg,.jpeg"
                        disabled={processing}
                        style={{ display: 'none' }}
                    />
                    
                    <div
                        ref={dragAreaRef}
                        className={`drag-drop-area ${dragActive ? 'drag-active' : ''} ${file ? 'has-file' : ''}`}
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        onClick={handleDragAreaClick}
                        role="button"
                        tabIndex={0}
                    >
                        <div className="drag-drop-content">
                            {file ? (
                                <>
                                    <FaFileAlt className="file-icon" />
                                    <div className="file-info">
                                        <div className="file-name">{file.name}</div>
                                        <div className="file-size">{formatFileSize(file.size)}</div>
                                    </div>
                                    <div className="file-change-text">Click to change file</div>
                                </>
                            ) : (
                                <>
                                    <FaCloudUploadAlt className="upload-icon" />
                                    <div className="drag-text">
                                        <strong>Drag and drop your file here</strong>
                                        <span>or click to browse</span>
                                    </div>
                                    <div className="supported-formats">
                                        Supported formats: PDF, PNG, JPG, JPEG (max 50MB)
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                
                <button 
                    type="submit" 
                    className="upload-button"
                    disabled={!file || processing}
                >
                    {processing ? (
                        <>
                            <FaSpinner className="spinner" />
                            Processing...
                        </>
                    ) : (
                        <>
                            <FaUpload />
                            Upload & Process
                        </>
                    )}
                </button>
            </form>
        </div>
    );
};

export default DocumentUpload; 